/**
 * Rate limit status card component.
 * Displays current rate limit usage and status.
 */

// React Imports
import { useState, useEffect } from 'react';
import { Chip } from '@coordio/ui';
import { Gauge as SpeedIcon, AlertTriangle as WarningIcon } from 'lucide-react';
import type { RateLimitStatus } from '../../types/connector';

export interface RateLimitStatusCardProps {
  status: RateLimitStatus;
  showDetails?: boolean;
}

/**
 * Rate limit status card component.
 */
export const RateLimitStatusCard = ({ status, showDetails = true }: RateLimitStatusCardProps) => {
  const [timeUntilReset, setTimeUntilReset] = useState<string>('');

  useEffect(() => {
    if (status.resetAt) {
      const updateTimer = () => {
        const resetDate = new Date(status.resetAt!);
        const now = new Date();
        const diff = resetDate.getTime() - now.getTime();

        if (diff > 0) {
          const minutes = Math.floor(diff / 60000);
          const seconds = Math.floor((diff % 60000) / 1000);
          setTimeUntilReset(`${minutes}m ${seconds}s`);
        } else {
          setTimeUntilReset('Resetting...');
        }
      };

      updateTimer();
      const interval = setInterval(updateTimer, 1000);
      return () => clearInterval(interval);
    }
  }, [status.resetAt]);

  const getUsagePercentage = (current: number, limit?: number) => {
    if (!limit || limit === 0) return 0;
    return Math.min((current / limit) * 100, 100);
  };

  const getProgressColor = (percentage: number) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 75) return 'bg-amber-500';
    return 'bg-green-500';
  };

  return (
    <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
      <div className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <SpeedIcon className="text-accent" />
          <h6 className="text-lg font-semibold">
            Rate Limit Status
          </h6>
          {status.isExceeded && (
            <Chip
              label="Exceeded"
              icon={<WarningIcon size={14} />}
              color="red"
              className="ml-auto"
            />
          )}
        </div>

        {status.isExceeded && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800/40 dark:bg-red-950/50 dark:text-red-300 mb-4">
            Rate limit exceeded. Please wait until {status.resetAt ? new Date(status.resetAt).toLocaleTimeString() : 'reset'}.
          </div>
        )}

        {showDetails && (
          <div className="flex flex-col gap-4">
            {/* Per Minute */}
            {status.rateLimitPerMinute !== undefined && (
              <div>
                <div className="flex justify-between mb-1">
                  <p className="text-sm text-muted-foreground">Per Minute</p>
                  <p className="text-sm font-medium">
                    {status.currentMinuteUsage} / {status.rateLimitPerMinute}
                  </p>
                </div>
                <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${getProgressColor(getUsagePercentage(status.currentMinuteUsage, status.rateLimitPerMinute))}`}
                    style={{ width: `${getUsagePercentage(status.currentMinuteUsage, status.rateLimitPerMinute)}%` }}
                  />
                </div>
              </div>
            )}

            {/* Per Hour */}
            {status.rateLimitPerHour !== undefined && (
              <div>
                <div className="flex justify-between mb-1">
                  <p className="text-sm text-muted-foreground">Per Hour</p>
                  <p className="text-sm font-medium">
                    {status.currentHourUsage} / {status.rateLimitPerHour}
                  </p>
                </div>
                <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${getProgressColor(getUsagePercentage(status.currentHourUsage, status.rateLimitPerHour))}`}
                    style={{ width: `${getUsagePercentage(status.currentHourUsage, status.rateLimitPerHour)}%` }}
                  />
                </div>
              </div>
            )}

            {/* Per Day */}
            {status.rateLimitPerDay !== undefined && (
              <div>
                <div className="flex justify-between mb-1">
                  <p className="text-sm text-muted-foreground">Per Day</p>
                  <p className="text-sm font-medium">
                    {status.currentDayUsage} / {status.rateLimitPerDay}
                  </p>
                </div>
                <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${getProgressColor(getUsagePercentage(status.currentDayUsage, status.rateLimitPerDay))}`}
                    style={{ width: `${getUsagePercentage(status.currentDayUsage, status.rateLimitPerDay)}%` }}
                  />
                </div>
              </div>
            )}

            {timeUntilReset && (
              <p className="text-xs text-muted-foreground mt-2">
                Resets in: {timeUntilReset}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
